import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { getPolicy } from '@/api/auth.api';
import type { PolicyLocale, ResolvedPolicyDocuments } from '@/types/policy';
import { resolvePolicyDocuments, STATIC_POLICY_DOCUMENTS } from '@/utils/policyDocuments';

type PolicyTermsState = {
  locale: PolicyLocale;
  documents: ResolvedPolicyDocuments;
};

export function useGetPolicyTerms() {
  // @ts-ignore
  const { i18n } = useTranslation();
  const locale = (['en', 'tc', 'sc'].includes(i18n.language) ? i18n.language : 'en') as PolicyLocale;
  const requestIdRef = useRef(0);
  const [policyState, setPolicyState] = useState<PolicyTermsState>({
    locale,
    documents: STATIC_POLICY_DOCUMENTS,
  });
  const [loadingLocale, setLoadingLocale] = useState<PolicyLocale | null>(locale);
  const [error, setError] = useState<string | null>(null);

  const isCurrentLocale = policyState.locale === locale;
  const urls = isCurrentLocale ? policyState.documents : STATIC_POLICY_DOCUMENTS;
  const loading = loadingLocale === locale || !isCurrentLocale;
  const initialized = isCurrentLocale && loadingLocale !== locale;

  const fetchPolicyTerms = useCallback(async () => {
    const requestId = ++requestIdRef.current;
    setPolicyState({ locale, documents: STATIC_POLICY_DOCUMENTS });
    setLoadingLocale(locale);
    setError(null);

    try {
      const response = await getPolicy(locale);
      if (!response.data.success) throw new Error(response.data.message || 'Policy API request failed.');

      const documents = await resolvePolicyDocuments(response.data.data || []);
      if (requestId === requestIdRef.current) setPolicyState({ locale, documents });
    } catch {
      if (requestId === requestIdRef.current) {
        setPolicyState({ locale, documents: STATIC_POLICY_DOCUMENTS });
        setError('Failed to fetch policy/terms');
      }
    } finally {
      if (requestId === requestIdRef.current) setLoadingLocale(null);
    }
  }, [locale]);

  useEffect(() => {
    void fetchPolicyTerms();
    return () => {
      requestIdRef.current += 1;
    };
  }, [fetchPolicyTerms]);

  const getPolicyTerms = useCallback(() => {
    if (!initialized) void fetchPolicyTerms();
    return urls;
  }, [fetchPolicyTerms, initialized, urls]);

  return {
    policyUrl: urls.policyUrl,
    policyDate: urls.policyDate,
    termsUrl: urls.termsUrl,
    termsDate: urls.termsDate,
    loading,
    error,
    getPolicyTerms,
    initialized,
  };
}
